import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Radio, Select } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useBeneficiariesActions, useBeneficiary } from '@/entities/beneficiary';
import { useApiBeneficiaries } from '@/entities/beneficiary';
import { useBeneficiariesPidActions } from '@/entities/beneficiary/hooks/useBeneficiariesPidActions';
import { useIwEditingStore } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import { useAddParticipantRole } from '@/entities/interwills/hooks/useAddParticipantRole';
import { AccountStatus } from '@/shared/api';
import { Input } from '@/shared/components';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { BeneficiaryDto, BeneficiaryGroup } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { Icon } from '@/shared/uikit/Icon';
import { objects } from '@/shared/utils/objects';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddWitnessModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { WitnessParams } from './types';

export const AddWitnessModal: FC<AddWitnessModalProps> = ({ witnessIndex }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'witness' });
  const { beneficiaries: apiBeneficiaries } = useApiBeneficiaries();

  const addRole = useAddParticipantRole();
  const [tab, setTab] = useState(0);

  const modals = useModalsStore();

  const {
    witnesses,
    beneficiaries: beneficiariesData,
    contingentBeneficiary,
    executor,
    subExecutor,
    editedEntities,
    updateWitness,
  } = useIwEditingStore();
  const sjDatabase = useSJDatabase();
  const { create } = useBeneficiariesActions();
  const { create: createPid } = useBeneficiariesPidActions();
  const { getBeneficiaryAdditionalInfo } = useBeneficiary();

  const beneficiaries = sjDatabase.collections.beneficiaries?.findMany(
    (item) =>
      item.id !== subExecutor?.id &&
      item.id !== executor?.id &&
      item.id !== contingentBeneficiary?.id &&
      !beneficiariesData.find((b) => b.id === item.id) &&
      !witnesses.find((w) => w.id === item.id),
  );

  useEffect(() => {
    if (!beneficiaries?.length) {
      setTab(1);
    }
  }, [beneficiaries]);

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm<WitnessParams>({
    defaultValues: {
      id: witnesses[witnessIndex]?.id,
    },
  });

  const selectedWitnessId = watch('id');

  const onSubmit = async (values: WitnessParams) => {
    let finalWitness: BeneficiaryDto | null = null;
    if (tab === 0) {
      const witness = beneficiaries?.find((n) => n.id === values.id);
      if (witness) {
        const centralBeneficiaryData = apiBeneficiaries?.find(
          (item) => item.id === witness?.centralDbProfileId,
        );
        const isExist = centralBeneficiaryData?.status === AccountStatus.ACTIVE;
        finalWitness = witness;

        const additionalInfo = getBeneficiaryAdditionalInfo(witness.id);
        updateWitness(
          witnessIndex,
          objects.copy({
            id: values.id,
            firstName: witness.firstName,
            lastName: witness.lastName,
            secondName: witness.secondName,
            email: witness.email,
            hasAccount: isExist,
            birthDate: additionalInfo?.birthday,
            gender: additionalInfo?.gender,
            ...editedEntities[values.id],
          }),
        );
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...filteredValues } = values;
      const newBen = await create({
        ...filteredValues,
        linkedCountryCode: '',
      });

      if (!newBen?.id) return;

      createPid({
        beneficiaryDtoId: newBen?.id,
        type: DocumentType.BENEFICIARY_PERSONAL_DATA,
        lastNameInNativeLanguage: '',
        nameInNativeLanguage: '',
      });

      finalWitness = newBen;
      updateWitness(
        witnessIndex,
        objects.copy({
          id: newBen.id,
          firstName: values.firstName,
          lastName: values.lastName,
          secondName: values.secondName,
          email: values.email,
          hasAccount: false,
        }),
      );
    }
    await addRole(finalWitness?.centralDbProfileId, 'WITNESS');
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={t(tab === 0 ? 'title' : 'titleNew')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          {tab === 0 && (
            <>
              <b>{t('selectText')}</b>
              <Radio.Group
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
                options={beneficiaries?.map((item) => ({
                  label: getBeneficiaryFullName(item),
                  value: item.id,
                }))}
                value={selectedWitnessId}
                onChange={(val) => {
                  setValue('id', val.target.value);
                }}
              />
              <ButtonIwText
                onClick={() => {
                  setTab(1);
                }}
                style={{ width: 'fit-content' }}
                icon={<Icon name="plus" size="s20" />}
              >
                {t('addNewBtn')}
              </ButtonIwText>
            </>
          )}
          {tab === 1 && (
            <>
              <Row>
                <Controller
                  name={'nickName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'firstName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
              <Row>
                <Controller
                  name={'secondName'}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'lastName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
              <Controller
                name={'group'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Select
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          {...field}
                          options={[
                            {
                              value: BeneficiaryGroup.FAMILY,
                              label: t('family'),
                            },
                            {
                              value: BeneficiaryGroup.FAMILY_CLAN,
                              label: t('familyClan'),
                            },
                            {
                              value: BeneficiaryGroup.CIRCLE,
                              label: t('circle'),
                            },
                          ]}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
              <Controller
                name={'email'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
            </>
          )}
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
`;
